import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { useTheme } from "../ThemeContext";
import {
  LayoutDashboard,
  FileText,
  GitBranch,
  Users,
  Shield,
  Settings,
  LogOut,
  Calendar,
  BarChart3,
  HelpCircle,
  Crown,
  Lock,
  Megaphone,
  X,
  PanelLeftClose,
} from "lucide-react";

const EASYOFFICE_LOGO = "https://customer-assets.emergentagent.com/job_contract-flow-21/artifacts/cuiv7qxm_eo-logo-blue-stacked-header-logo.png";

const TEAM_PLANS = ["team", "enterprise"];

const Sidebar = ({ mobileOpen, onClose, collapsed, onToggleCollapse }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { companyLogo } = useTheme();

  const hasTeamPlan = TEAM_PLANS.includes(user?.plan_id);

  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/transactions", label: "Transactions", icon: FileText },
    { path: "/contacts", label: "Contacts", icon: Users },
    { path: "/calendar", label: "Calendar", icon: Calendar },
    { path: "/reports", label: "Reports", icon: BarChart3, requiresTeam: true },
  ];

  if (user?.role === "super_admin") {
    navItems.push({ path: "/admin", label: "Users", icon: Shield });
  }

  navItems.push({ path: "/workflows", label: "Workflows", icon: GitBranch });
  navItems.push({ path: "/settings", label: "Settings", icon: Settings });
  navItems.push({ path: "/help", label: "Help Center", icon: HelpCircle });

  if (user?.is_platform_admin) {
    navItems.push({ path: "/platform-admin", label: "Platform Admin", icon: Crown });
    navItems.push({ path: "/ad-content", label: "Ad Content", icon: Megaphone });
  }

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const logoSrc = hasTeamPlan && companyLogo ? companyLogo : EASYOFFICE_LOGO;
  const isCustomLogo = hasTeamPlan && !!companyLogo;

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  const sidebarContent = (
    <aside className="w-64 flex flex-col h-screen" style={{ background: 'var(--t-sidebar)' }}>
      {/* Logo */}
      <div className="p-6 flex items-start justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div>
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="sidebar-logo" onClick={handleNavClick}>
            <img 
              src={logoSrc}
              alt="Company Logo"
              className={`h-10 w-auto ${isCustomLogo ? "" : "brightness-0 invert"}`}
              style={{ maxWidth: '180px' }}
            />
          </Link>
          <p className="text-[10px] mt-2 font-medium tracking-[0.12em] uppercase" style={{ color: 'var(--t-sidebar-text)' }}>
            Transaction Manager
          </p>
          {user?.org_name && (
            <p className="text-[10px] mt-0.5 font-medium tracking-[0.08em]" style={{ color: 'var(--t-sidebar-text)', opacity: 0.7 }}>
              {user.org_name}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 -mr-1 -mt-1">
          {/* Desktop collapse button */}
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="hidden lg:flex items-center justify-center w-7 h-7 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              title="Collapse sidebar"
              data-testid="sidebar-collapse-btn"
            >
              <PanelLeftClose className="w-4 h-4" />
            </button>
          )}
          {/* Mobile close button */}
          {onClose && (
            <button onClick={onClose} className="lg:hidden text-white/60 hover:text-white p-1" data-testid="sidebar-close">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const locked = item.requiresTeam && !hasTeamPlan;
          if (locked) {
            return (
              <Link
                key={item.path}
                to="/settings#subscription-section"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="sidebar-link opacity-50"
                title="Upgrade to Team plan to unlock"
                onClick={handleNavClick}
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
                <span className="flex-1">{item.label}</span>
                <Lock className="w-3 h-3" />
              </Link>
            );
          }
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`sidebar-link ${isActive(item.path) ? "active" : ""}`}
              onClick={handleNavClick}
            >
              <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3 mb-3">
          {user?.picture ? (
            <img 
              src={user.picture} 
              alt={user.name} 
              className="w-9 h-9 rounded-full border-2"
              style={{ borderColor: 'var(--t-primary)' }}
            />
          ) : (
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold" style={{ background: 'var(--t-primary)' }}>
              {user?.name?.charAt(0) || "U"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-[11px] truncate" style={{ color: 'var(--t-sidebar-text)' }}>
              {user?.role === "super_admin" ? "Admin" : "Coordinator"}
            </p>
          </div>
        </div>
        <button
          onClick={() => { handleNavClick(); logout(); }}
          data-testid="logout-btn"
          className="sidebar-link w-full"
        >
          <LogOut className="w-[18px] h-[18px]" strokeWidth={1.5} />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );

  // Desktop: static sidebar (hideable)
  // Mobile: overlay drawer
  return (
    <>
      {/* Desktop sidebar */}
      {!collapsed && (
        <div className="hidden lg:block sticky top-0 h-screen flex-shrink-0 w-64">
          {sidebarContent}
        </div>
      )}

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50" onClick={onClose} data-testid="sidebar-backdrop" />
          {/* Drawer */}
          <div className="relative z-50 animate-in slide-in-from-left duration-200">
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
